import { validate } from 'validate.js';
import * as errorKeys from 'constants/errors';
import requireTrueBaseValidator from 'forms/shared/validators/base/requireTrue';
import requireEmptyBaseValidator from 'forms/shared/validators/base/requireEmpty/requireEmpty';
import hasValueBaseValidator from 'forms/shared/validators/base/hasValue';
import arrayBaseValidator from 'forms/shared/validators/base/array/array';
import accordionBaseValidator from 'forms/shared/validators/base/accordion';
import branchCollectionBaseValidator from 'forms/shared/validators/base/branchCollection';
import modelBaseValidator from 'forms/shared/validators/base/model';
import dateBaseValidator from 'forms/shared/validators/base/date';
import daterangeBaseValidator from 'forms/shared/validators/base/dateRange';
import locationBaseValidator from 'forms/shared/validators/base/location';
import ssnBaseValidator from 'forms/shared/validators/base/ssn';
import zipcodeBaseValidator from 'forms/shared/validators/base/zipcode';
import countryBaseValidator from 'forms/shared/validators/base/country';
import durationCoverageBaseValidator from 'forms/shared/validators/base/durationCoverage';
import containsRequiredItemsBaseValidator from 'forms/shared/validators/base/containsRequiredItems';
import requiredBaseValidator from 'forms/shared/validators/base/required';
import {
  isDateTime,
  cleanDateObject,
  createDateFromObject,
  createDateFromTimestamp,
} from 'helpers/date';

// Error message format
validate.formatters.errorKeys = (errors) => errors.map((e) => e);
// errors.map(e => `${e.attribute}.${e.validator}.${e.error}`)

// Date parser/formatting
validate.extend(validate.validators.datetime, {
  parse: (value) => {
    // Value is the datetime as taken in by the form
    // Return unix timestamp
    const dateTime = value && isDateTime(value) ? value : createDateFromObject(cleanDateObject(value));

    if (dateTime.isValid) {
      return dateTime.toMillis();
    }

    return NaN;
  },
  format: (value) => {
    // Value is the datetime as a unix timestamp
    // Return formatted date (used in error messages)
    const dateTime = createDateFromTimestamp(value);
    return dateTime.toLocaleString();
  },
});

// Set default options/config
validate.validators.datetime.notValid = errorKeys.INVALID_DATE;
validate.validators.datetime.tooEarly = errorKeys.DATE_TOO_EARLY;
validate.validators.datetime.tooLate = errorKeys.DATE_TOO_LATE;
validate.validators.email.message = errorKeys.INVALID_EMAIL;
validate.validators.equality.message = errorKeys.NOT_EQUAL;
validate.validators.exclusion.message = errorKeys.EXCLUSION;
validate.validators.format.message = errorKeys.INVALID_FORMAT;
validate.validators.inclusion.message = errorKeys.INCLUSION;
validate.validators.length.notValid = errorKeys.INVALID_LENGTH;
validate.validators.length.wrongLength = errorKeys.LENGTH_WRONG;
validate.validators.length.tooShort = errorKeys.LENGTH_TOO_SHORT;
validate.validators.length.tooLong = errorKeys.LENGTH_TOO_LONG;
validate.validators.numericality.notValid = errorKeys.INVALID_NUMBER;
validate.validators.numericality.notInteger = errorKeys.NUMBER_NOT_INTEGER;
validate.validators.numericality.notGreaterThan = errorKeys.NUMBER_NOT_GREATER_THAN;
validate.validators.numericality.notGreaterThanOrEqualTo = errorKeys.NUMBER_NOT_GREATER_THAN_OR_EQUAL_TO;
validate.validators.numericality.notEqualTo = errorKeys.NUMBER_NOT_EQUAL_TO;
validate.validators.numericality.notLessThan = errorKeys.NUMBER_NOT_LESS_THAN;
validate.validators.numericality.notLessThanOrEqualTo = errorKeys.NUMBER_NOT_LESS_THAN_OR_EQUAL_TO;
validate.validators.numericality.notDivisibleBy = errorKeys.NUMBER_NOT_DIVISIBLE_BY;
validate.validators.numericality.notOdd = errorKeys.NUMBER_NOT_ODD;
validate.validators.numericality.notEven = errorKeys.NUMBER_NOT_EVEN;
validate.validators.presence.options = { allowEmpty: false };
validate.validators.presence.message = errorKeys.REQUIRED;
validate.validators.url.message = errorKeys.INVALID_URL;

validate.options = {
  fullMessages: false,
  format: 'errorKeys',
  allowPOBox: true,
  militaryAddress: true,
  requireDay: true,
  requireMonth: true,
  requireYear: true,
  earliest: createDateFromObject({ day: 1, month: 1, year: 1000 }),
  latest: createDateFromObject({ day: 1, month: 1, year: 10000 }),
};

/**
 * @returns {boolean | import('forms/shared/types').ValidateErrorContext[]}
 */
export const validateModel = (data = {}, model, options) => {
  const errors = options ? validate(data, model, options) : validate(data, model);

  if (!errors) { return true; }

  return errors;
};

// Implement custom validators
// * The below validators were converted into curry functions, to allow passing
//   of validateModel/validate into them to minimize circular dependencies. The
//   previous iteration had an import loop of:
//  - validate.js (validateModel) ->  validateConfig.js (validate) ->
//    dateValidator -> validate.js (validateModel)
// * As that is a circular dependency, it can hard break your local workspace
//   when changes are made to those files.

validate.validators.requireTrue = requireTrueBaseValidator(validateModel, validate);
validate.validators.requireEmpty = requireEmptyBaseValidator(validateModel, validate);
validate.validators.hasValue = hasValueBaseValidator(validateModel, validate);
validate.validators.array = arrayBaseValidator(validateModel, validate);
validate.validators.accordion = accordionBaseValidator(validateModel, validate);
validate.validators.branchCollection = branchCollectionBaseValidator(validateModel, validate);
validate.validators.model = modelBaseValidator(validateModel, validate);
validate.validators.date = dateBaseValidator(validateModel, validate);
validate.validators.daterange = daterangeBaseValidator(validateModel, validate);
validate.validators.location = locationBaseValidator(validateModel, validate);
validate.validators.ssn = ssnBaseValidator(validateModel, validate);
validate.validators.zipcode = zipcodeBaseValidator(validateModel, validate);
validate.validators.country = countryBaseValidator(validateModel, validate);
validate.validators.durationCoverage = durationCoverageBaseValidator(validateModel, validate);
validate.validators.containsRequiredItems = containsRequiredItemsBaseValidator(
  validateModel,
  validate
);
validate.validators.required = requiredBaseValidator(validateModel, validate);

export default validate;
